import type { McapTypes } from "@mcap/core";

import type { MessagePathPart } from "@/shared/domain/topics";
import type { MessagePayload } from "@/shared/webworker";

import type { ImageMessage, SourceEntry } from "../ImageLoader";

export enum ImageCommand {
  Init = "Init",
  IndexSource = "IndexSource",
  LoadImages = "LoadImages",
}

export interface ImageCommandPayloadMap {
  /**
   * Reset the worker: discard any accumulated sources and ready the HTTP
   * client. Sent once before the main thread schedules the per-source indexing.
   */
  [ImageCommand.Init]: Record<string, never>;
  /**
   * Index one source (read its MCAP footer/summary into chunk indices).
   * `mappings` is present when the main thread resolved the topic's candidate
   * representations from hydrated workspace state; when absent, the worker
   * falls back to the per-topic REST lookup.
   */
  [ImageCommand.IndexSource]: SourceEntry;
  [ImageCommand.LoadImages]: {
    startTimeNs: bigint;
    endTimeNs: bigint;
    format: string;
    messagePath: MessagePathPart[];
  };
}

export enum ImageCommandResponse {
  Initialized = "Initialized",
  SourceIndexed = "SourceIndexed",
  ImagesLoaded = "ImagesLoaded",
}

export interface ImageCommandResponsePayloadMap {
  /** Acknowledges the reset; the worker is ready for `IndexSource` commands. */
  [ImageCommandResponse.Initialized]: Record<string, never>;
  /**
   * The indexed source's chunk indices (for the main thread to merge in) and
   * how many readers actually opened. A source can open yet be empty, so
   * `openedSourceCount` — not the chunk indices — is what tells the main thread
   * whether the topic had a decodable representation.
   */
  [ImageCommandResponse.SourceIndexed]: {
    chunkIndices: readonly McapTypes.ChunkIndex[];
    openedSourceCount: number;
  };
  [ImageCommandResponse.ImagesLoaded]: { messages: ImageMessage[] };
}

export function isInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<ImageCommand, ImageCommandPayloadMap[ImageCommand.Init]>
> {
  return event.data.type === ImageCommand.Init;
}

export function isIndexSourceCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<ImageCommand, ImageCommandPayloadMap[ImageCommand.IndexSource]>
> {
  return event.data.type === ImageCommand.IndexSource;
}

export function isLoadImagesCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<ImageCommand, ImageCommandPayloadMap[ImageCommand.LoadImages]>
> {
  return event.data.type === ImageCommand.LoadImages;
}
